import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/ui/skeleton';
import { FailureNotice } from '@/components/FailureNotice';
import { presentFailure } from '@/lib/api/failure';

export interface AsyncSectionProps<T> {
  /** What is being loaded, for the accessible loading announcement. */
  readonly label: string;
  readonly isPending: boolean;
  readonly error: unknown;
  readonly data: T | undefined;
  readonly onRetry?: (() => void) | undefined;
  /** Number of skeleton rows. Omit when the final layout is not known. */
  readonly skeletonRows?: number | undefined;
  /** Rendered when `data` resolves. Receives the non-undefined value. */
  readonly children: (data: T) => ReactNode;
  /**
   * `true` when the data shown is stale from cache while a refetch runs —
   * design doc §22.4's "partial data with freshness timestamp".
   */
  readonly isStale?: boolean | undefined;
  readonly updatedAt?: number | undefined;
}

/**
 * The §22.4 state machine, implemented once.
 *
 * Loading uses skeletons only when `skeletonRows` says the layout is known,
 * and a compact progress state otherwise; errors are classified as retriable
 * or terminal before they reach the user; stale-but-shown data carries a
 * freshness timestamp rather than pretending to be current.
 */
export function AsyncSection<T>({
  label,
  isPending,
  error,
  data,
  onRetry,
  skeletonRows,
  children,
  isStale,
  updatedAt,
}: AsyncSectionProps<T>) {
  const { t, i18n } = useTranslation();

  if (error !== null && error !== undefined) {
    const failure = presentFailure(error);
    if (failure) return <FailureNotice failure={failure} onRetry={onRetry} />;
    return (
      <FailureNotice
        failure={{
          kind: 'terminal',
          guidanceKey: 'errors.guidance.unexpected',
          code: 'UNEXPECTED',
          detail: error instanceof Error ? error.message : String(error),
          requestId: undefined,
          failure: {
            kind: 'malformed',
            status: 0,
            message: '',
            requestId: undefined,
            body: '',
            retriable: false,
          },
        }}
      />
    );
  }

  if (isPending || data === undefined) {
    return (
      <div role="status" aria-live="polite" aria-busy="true" className="flex flex-col gap-2">
        <span className="sr-only">{t('common.loadingWhat', { what: label })}</span>
        {skeletonRows === undefined ? (
          <p className="text-sm text-muted-foreground">
            {t('common.loadingWhat', { what: label })}
          </p>
        ) : (
          Array.from({ length: skeletonRows }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))
        )}
      </div>
    );
  }

  return (
    <>
      {isStale && updatedAt !== undefined ? (
        <p className="mb-2 text-xs text-muted-foreground" data-testid="freshness">
          {t('common.freshness', {
            timestamp: new Intl.DateTimeFormat(i18n.language, { timeStyle: 'medium' }).format(
              new Date(updatedAt),
            ),
          })}
        </p>
      ) : null}
      {children(data)}
    </>
  );
}
